/**
 * Canonical memory categories.
 *
 * - `user`      — facts about the operator (role, preferences, working style).
 * - `feedback`  — corrections and validated approaches from the operator.
 * - `project`   — ongoing work context (goals, deadlines, stakeholders).
 * - `reference` — pointers to external systems (dashboards, repos, channels).
 *
 * Hosts may extend the set, but stability of these four is preserved.
 */
export const MemoryType = {
  User: "user",
  Feedback: "feedback",
  Project: "project",
  Reference: "reference",
} as const;

export type MemoryType = (typeof MemoryType)[keyof typeof MemoryType];

/**
 * Frontmatter required on every memory file.
 *
 * `name` should match the file id (filename without `.md`). `description`
 * is a single-line summary used in the generated index. `type` places the
 * memory in one of the canonical categories. Additional keys (e.g.
 * `originSessionId`, `created`, custom tags) are tolerated and preserved
 * on round-trip but are not required.
 */
export interface MemoryFrontmatter {
  name: string;
  description: string;
  type: MemoryType;
  [key: string]: unknown;
}

/**
 * A parsed memory document, ready to be written back or rendered.
 *
 * `id` is the filename stem (no `.md`); it is the address by which the
 * store retrieves and overwrites the memory.
 */
export interface Memory {
  id: string;
  frontmatter: MemoryFrontmatter;
  body: string;
}
